/* eslint-disable no-unused-vars, react/prop-types, react-hooks/exhaustive-deps */
import React, { Suspense, useState, useEffect, useCallback } from "react";
import { Box, Grid2, CircularProgress, Typography } from "@mui/material";
import SupplierCard from "../../../Components/SupplierCards";
import SkeletonLoader from "../../../Components/SupplierCards/SkeletonLoader";
import SupplierProfileModal from "../../SupplierProfileModal";
import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import { useTranslation } from "react-i18next";
import CommunitySuppliersHeaderFilters from "../../../Components/CommunitySuppliersHeaderFilters";
import CustomFilterTabs from "../../../Components/CustomFilterTabs";
import { sortCategoriesWithSubcategories } from "../../../Utils/SortData";
import {
  SUPPLIER_ITEM_TYPES,
  DEFAULT_ITEM_TYPE,
  itemTypeConfig,
} from "./itemTypes";
// ⚠️ TEMP DEV-PHASE PREVIEW — remove with the preview seed.
import {
  isSupplierIndexPreview,
  PREVIEW_SUPPLIERS,
  PREVIEW_CATEGORIES,
  PREVIEW_COUNTS,
  filterPreviewSuppliers,
} from "./previewData";

const emptyStateBox = {
  width: "100%",
  padding: "48px 24px",
  textAlign: "center",
};
const emptyStateTitle = {
  fontSize: "16px",
  fontWeight: 500,
  color: "rgba(62, 63, 76, 1)",
  lineHeight: "160%",
};
const emptyStateText = {
  fontSize: "13px",
  color: "rgba(62, 63, 76, 0.6)",
  marginTop: "4px",
};

function FindSupplier() {
  const delay = process.env.DEBOUNCE_DELAY;
  const [isLoading, setIsLoading] = useState(false);
  const [itemsToShow, setItemsToShow] = useState(50);
  const [activePage, setActivePage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [originalSuppliers, setOriginalSuppliers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [categories, setCategories] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showSupplierProfile, setShowSupplierProfile] = useState(false);
  const [supplierProfileData, setSupplierProfileData] = useState(null);
  const [refreshSuppliersData, setRefreshSuppliersData] = useState(false);
  // Which catalog is open. Each tab is its own search: its own category tree,
  // its own filters, its own results — switching starts clean rather than
  // carrying a plumber search into restaurants.
  const [itemType, setItemType] = useState(DEFAULT_ITEM_TYPE);
  const [countsByType, setCountsByType] = useState({});
  const [selectedCommunityStatus, setSelectedCommunityStatus] = useState(null);

  const { t } = useTranslation("page-find-new-suppliers");

  const fetchSuppliers = async () => {
    try {
      const response = await apiGetRequest(
        endpoints.suppliers({
          page: activePage,
          pageSize: itemsToShow,
          searchValue: searchValue,
          categoryIds: selectedCategories,
          zoneIds: selectedLocations,
          itemType,
        })
      );
      setSuppliers(response?.data?.suppliers || []);
      setOriginalSuppliers(response?.data?.suppliers || []);
      setMaxPage(response?.data?.max_page_number ?? 1);
    } catch (error) {
      // ⚠️ TEMP DEV-PHASE PREVIEW — sample suppliers per tab while there is no
      // backend to answer. Remove with the preview seed.
      if (isSupplierIndexPreview()) {
        const rows = filterPreviewSuppliers(itemType, {
          searchValue,
          selectedCategories,
        });
        setSuppliers(rows);
        setOriginalSuppliers(rows);
        setMaxPage(1);
      }
      console.log(error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [suppliersResult, zonesResult, categoriesResult, countsResult] =
        await Promise.allSettled([
          fetchSuppliers(),
          apiGetRequest(endpoints.getZones),
          // The open tab's own category tree — service, product and restaurant
          // categories are three separate trees.
          apiGetRequest(itemTypeConfig(itemType).categoriesEndpoint),
          apiGetRequest(endpoints.getSupplierCountsByType),
        ]);

      if (zonesResult.status === "fulfilled") {
        setZones(zonesResult.value.data || []);
      }

      if (categoriesResult.status === "fulfilled") {
        let categories = categoriesResult.value.data || [];

        categories = sortCategoriesWithSubcategories(categories);
        setCategories(categories);
      } else if (isSupplierIndexPreview()) {
        // ⚠️ TEMP DEV-PHASE PREVIEW — the store and restaurant trees don't
        // exist on the backend yet, so the tabs show sample ones.
        setCategories(
          sortCategoriesWithSubcategories(PREVIEW_CATEGORIES[itemType] || []),
        );
      } else {
        setCategories([]);
      }

      if (countsResult.status === "fulfilled") {
        setCountsByType(countsResult.value.data || {});
      } else if (isSupplierIndexPreview()) {
        setCountsByType(PREVIEW_COUNTS);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Switching tabs is a new search, not a refinement: filters and search reset,
  // and the next tab loads its own categories.
  const handleItemTypeChange = (nextType) => {
    if (nextType === itemType) return;
    setItemType(nextType);
    setSelectedCategories([]);
    setSelectedLocations([]);
    setSearchValue("");
    setSuppliers([]);
    setCategories([]);
    setActivePage(1);
  };

  const getSuppliers = async (pageNumb, isFilterChange = false) => {
    try {
      setIsLoading(true);
      //console.log('pagenumber', pageNumb ?? activePage);

      const response = await apiGetRequest(
        endpoints.suppliers({
          page: pageNumb ?? activePage,
          pageSize: itemsToShow,
          searchValue: searchValue,
          categoryIds: selectedCategories,
          zoneIds: selectedLocations,
          itemType,
        })
      );

      //setSuppliers(response?.data?.suppliers || []);
      setSuppliers((prevSuppliers) => {
        if (isFilterChange) {
          return response?.data?.suppliers || [];
        }
        const uniqueSuppliers = [
          ...prevSuppliers,
          ...response?.data?.suppliers,
        ].filter(
          (supplier, index, self) =>
            index === self.findIndex((s) => s.id === supplier.id)
        );
        return uniqueSuppliers;
      });

      setMaxPage(response?.data?.max_page_number ?? 1);
    } catch (error) {
      // ⚠️ TEMP DEV-PHASE PREVIEW — see fetchSuppliers above.
      if (isSupplierIndexPreview()) {
        setSuppliers(
          filterPreviewSuppliers(itemType, { searchValue, selectedCategories }),
        );
        setMaxPage(1);
      }
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType]);

  // Handle Infinite Scroll
  const handleScroll = useCallback(() => {
    if (isLoading) return;

    const scrollPosition = window.innerHeight + window.scrollY;
    const threshold = document.body.offsetHeight - 200;

    if (scrollPosition >= threshold && activePage < maxPage) {
      setActivePage((prev) => prev + 1);
    }
  }, [isLoading, activePage, maxPage]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setActivePage(1);
      getSuppliers(1, true);
    }, delay);

    return () => clearTimeout(delayDebounce);
  }, [searchValue, selectedCategories, selectedLocations]);

  useEffect(() => {
    getSuppliers(activePage);
  }, [activePage]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const clearFilters = () => {
    setSelectedCategories("");
    setSelectedLocations("");
    setSearchValue("");
  };

  const displaySupplierProfile = (item) => {
    console.log("opening profile", item);
    // A supplier the community already works with (or is inviting) turns up in
    // the tabs of their other catalogs. Inviting is per supplier, so the profile
    // opens as an invite that can't be sent, rather than as the management view.
    setSelectedCommunityStatus(item?.community_status || null);
    getSupplierProfileData(item?.id);
    setShowSupplierProfile(true);
  };

  const getSupplierProfileData = async (supplier_id) => {
    try {
      const response = await apiGetRequest(
        endpoints.getSupplierProfilePricelists(supplier_id)
      );
      setSupplierProfileData(response?.data || {});
    } catch (err) {
      console.log("error: ", err);
    }
  };

  const handleCloseSupplierProfileModal = () => {
    setShowSupplierProfile(false);
    setSupplierProfileData(null);
    setSelectedCommunityStatus(null);
  };

  useEffect(() => {
    if (refreshSuppliersData) {
      setIsLoading(true);
      fetchSuppliers();
      setRefreshSuppliersData(false);
      setIsLoading(false);
    }
  }, [refreshSuppliersData]);

  const hasFilters =
    selectedLocations.length > 0 ||
    selectedCategories.length > 0 ||
    searchValue.trim() !== "";

  return (
    <>
      <Box>
        {/* What the manager is looking for comes first — someone to do a job,
            a shop, or a restaurant. Each tab carries its own filters. */}
        <Box sx={{ minWidth: 0, maxWidth: "100%", mb: "16px" }}>
          <CustomFilterTabs
            scrollable
            tabs={SUPPLIER_ITEM_TYPES.map((type) => ({
              label: t(type.labelKey),
              value: type.key,
              count: countsByType[type.key],
            }))}
            activeTab={itemType}
            onChange={(_, value) => handleItemTypeChange(value)}
          />
        </Box>

        <Suspense>
          <CommunitySuppliersHeaderFilters
            {...{
              searchValue,
              setSearchValue,
              originalSuppliers,
              setSuppliers,
              clearFilters,
              selectedCategories,
              setSelectedCategories,
              selectedLocations,
              setSelectedLocations,
              categories,
              categoriesNamespace: itemTypeConfig(itemType).categoriesNamespace,
              zones,
              hasFilters,
              showMobileFilters: false,
            }}
          />
        </Suspense>

        <Grid2
          container
          columnSpacing={3}
          rowSpacing={2}
          id="supplier-container"
        >
          {isLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <Grid2
                size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4, "2xl": 3 }}
                key={index}
                sx={{ display: "flex", flexWrap: "wrap" }}
              >
                <SkeletonLoader />
              </Grid2>
            ))
          ) : !suppliers || !Array.isArray(suppliers) || suppliers.length === 0 ? (
            /* An empty tab is normal early on — a community can be full of
               tradespeople and have no restaurants yet. It says so, and points
               at the thing the manager can actually do about it. */
            <Box sx={emptyStateBox}>
              <Typography sx={emptyStateTitle}>
                {hasFilters
                  ? t("empty.filtered.title")
                  : t(`${itemTypeConfig(itemType).emptyKey}.title`)}
              </Typography>
              <Typography sx={emptyStateText}>
                {hasFilters
                  ? t("empty.filtered.text")
                  : t(`${itemTypeConfig(itemType).emptyKey}.text`)}
              </Typography>
            </Box>
          ) : (
            suppliers.map((supplier, index) => (
              <Grid2
                size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4, "2xl": 3 }}
                key={index}
                sx={{ display: "flex", flexWrap: "wrap" }}
              >
                <SupplierCard
                  supplier={supplier}
                  itemType={itemType}
                  displaySupplierProfile={displaySupplierProfile}
                  buttonText={t("supplier_card_button_text")}
                />
              </Grid2>
            ))
          )}
          {isLoading && <CircularProgress />}
        </Grid2>
      </Box>

      {showSupplierProfile && supplierProfileData && (
        <SupplierProfileModal
          {...{
            viewType:
              selectedCommunityStatus
                ? "invite"
                : supplierProfileData.is_invited === true
                  ? "view"
                  : "invite",
            inviteBlockedReason: selectedCommunityStatus,
            showDiscountBadge: false,
            openSupplierProfileModal: showSupplierProfile,
            supplierProfileData,
            handleCloseSupplierProfileModal,
            setRefreshSuppliersData,
          }}
        />
      )}
    </>
  );
}

export default FindSupplier;
